import { useCallback, useEffect, useState } from 'react';
import ReactConfetti from 'react-confetti';
import { useRouter } from 'next/router';

import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';
import { intercomShowNewMessage } from 'lib/intercom';
import { pageRoutes } from 'lib/routes';
import { Offer, OFFER_STATUS } from 'types';
import { ONE_DAY, ONE_HOUR, ONE_MINUTE, ONE_SECOND } from 'utils/date';

import { PriceLabel } from './PriceLabel';

import { YourWatch } from '../common/summary/YourWatch';

type OfferAcceptedModalProps = {
  offer: Offer;
  revalidateOffer?: () => void;
  isSeller?: boolean;
} & SlideoutCommonProps;

export const OfferAcceptedModal: React.FC<OfferAcceptedModalProps> = ({
  offer,
  closeModal,
  isOpen,
  revalidateOffer,
  isSeller
}) => {
  const router = useRouter();
  const [time, setTime] = useState(Date.now());

  const formatDate = (number: number) => ('0' + number).slice(-2);

  const expirationDate = new Date(offer.expiration);
  const timeToExpire = Math.max(0, offer.expiration ? expirationDate.valueOf() - time : 0);
  const daysToExpire = formatDate(Math.floor(timeToExpire / ONE_DAY));
  const hoursToExpire = formatDate(Math.floor((timeToExpire % ONE_DAY) / ONE_HOUR));
  const minutesToExpire = formatDate(Math.floor((timeToExpire % ONE_HOUR) / ONE_MINUTE));
  const secondsToExpire = formatDate(Math.floor((timeToExpire % ONE_MINUTE) / ONE_SECOND));
  const timeLabel = `${
    +daysToExpire > 0 ? daysToExpire.toString() + ':' : ''
  }${hoursToExpire}:${minutesToExpire}:${secondsToExpire}`;

  useEffect(() => {
    if (timeToExpire > 0 && offer.status === OFFER_STATUS.ACCEPTED) {
      const interval = setInterval(() => {
        setTime(Date.now());
        if (timeToExpire <= 0) {
          clearInterval(interval);
          revalidateOffer?.();
          closeModal?.();
        }
      }, 1000);

      return () => {
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickCheckout = useCallback(() => {
    router.push(pageRoutes.listingCheckout(offer.listing.id));
  }, [router, offer]);

  const submitProps = {
    title: isSeller ? (
      'Questions? Ask us'
    ) : (
      <div className='d-flex justify-content-between w-100 align-items-center'>
        <div className='text-white text-start fs-16px ms-3 me-2 white-space-nowrap'>
          Checkout
          <span />
        </div>
        <div className='text-light text-end me-3 opacity-50'>{timeLabel}</div>
      </div>
    ),
    onClick: isSeller ? intercomShowNewMessage : onClickCheckout,
    textClassname: 'w-100'
  };

  return (
    <Slideout submit={submitProps} closeModal={closeModal} isOpen={isOpen} title={<span />}>
      <ReactConfetti width={500} height={1000} />
      <div className='d-flex flex-column gap-5'>
        <YourWatch className='fs-14px pt-sm-3 pb-sm-1 py-1' listing={offer.listing} />
        <div className=' p-4 pt-2 riforma-regular'>
          <div className='d-flex justify-content-center mb-5'>
            <PriceLabel price={offer?.priceCents} className='mx-auto' />
          </div>
          <div className='pb-4'>
            <div className='fs-24px line-height-32px text-black opacity-90 text-center text-primary pb-5'>
              Offer accepted
            </div>
            <div className='fs-16px line-height-24px text-center opacity-60'>
              {isSeller
                ? 'We just alerted the buyer! The listing was automatically updated and they have 1 business day to pay for the watch.'
                : 'Congrats, your offer was accepted by the seller! You now have 24 hours to submit payment for your order.'}
            </div>
          </div>
        </div>
      </div>
    </Slideout>
  );
};
